import { IconHelpCircle } from "@tabler/icons-react";
import { useEffect, useState } from "react";

import * as helpcrunch from "~/components/chat-button/helpcrunch";
import { useAuthorization } from "~/contexts/authorizationProvider";
import { useOrg } from "~/contexts/orgProvider";
import { useUser } from "~/contexts/userProvider";
import { notificationsService } from "~/services/notifications.service";
import { ActionIcon, Indicator, Tooltip, useLocalStorage } from "~/ui";

type HelpMessage = {
  edited: boolean;
  text: string;
  type: string;
};

export const ChatButton = () => {
  const { userId, userEmail, userFirstName, userLastName, userClerkId } = useUser();
  const { orgId, orgName } = useOrg();
  const { role } = useAuthorization();

  const [ready, setReady] = useState<boolean>(false);
  const [unread, setUnread] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [seenBefore, setSeenBefore] = useLocalStorage({ key: "intercomSeenBefore", defaultValue: false });

  const sendNotification = async (message: string) => {
    await notificationsService.triggerNotification("ShelfCycle Help", `You have a new ShelfCycle help message: ${message}`, () => {
      helpcrunch.show();
    });
  };

  const registerHelpcrunchHooks = () => {
    helpcrunch.onUnreadChat((count: number) => {
      setUnread(count > 0);
    });
    helpcrunch.onIncomingMessage((helpMessage: HelpMessage) => {
      if (isChatOpen) {
        return;
      }
      if (helpMessage.type !== "message" && !helpMessage.edited) {
        return;
      }
      sendNotification(helpMessage.text);
    });
    helpcrunch.onChatOpen(() => setIsChatOpen(true));
    helpcrunch.onChatClose(() => setIsChatOpen(false));
  };

  useEffect(() => {
    if (ready) {
      registerHelpcrunchHooks();
      return;
    }
    helpcrunch.init(() => {
      setReady(true);
    });
  }, [ready]);

  useEffect(() => {
    if (!userId) {
      return;
    }

    helpcrunch.updateUser({
      clerkId: userClerkId,
      email: userEmail,
      name: `${userFirstName} ${userLastName}`,
      orgId,
      orgName,
      role,
      userId,
    });
  }, [userId, userEmail, userFirstName, userLastName, userClerkId, orgId, orgName, role]);

  return ready ? (
    <Tooltip label="Click here for help" position="left" withArrow>
      <ActionIcon
        aria-label="Help"
        color="gray"
        size={44}
        radius="sm"
        onClick={() => {
          helpcrunch.show();
          setSeenBefore(true);
          notificationsService.requestNotificationsPermission();
        }}
      >
        <Indicator offset={9} color="#74ADFC" disabled={!unread && seenBefore} size="14px" w={30} h={30}>
          <IconHelpCircle size={30} />
        </Indicator>
      </ActionIcon>
    </Tooltip>
  ) : (
    <></>
  );
};
